/**
 * ============================================================================
 * 🔒🔒🔒 PROTECTED FILE - DO NOT MODIFY WITHOUT OWNER APPROVAL 🔒🔒🔒
 * ============================================================================
 * 
 * FILE: barcodeValidator.js
 * LAST VERIFIED WORKING: January 2025
 * VERIFIED BY: Owner (garry.yee13@gmail.com)
 * 
 * Barcode Validation Utilities
 * 
 * Validates EAN-13, EAN-8, UPC-A barcodes using checksum verification
 * to prevent false positives from scanner misreads.
 * 
 * SUPPORTED FORMATS:
 * - EAN-13 (13 digits) - European Article Number
 * - EAN-8 (8 digits) - Short form EAN
 * - UPC-A (12 digits) - Universal Product Code
 * 
 * ⚠️ DO NOT:
 * - Change the checksum calculation algorithms
 * - Remove validation for any format
 * - Bypass validation checks
 * 
 * If issues occur, contact the owner before making changes.
 * ============================================================================
 */

/**
 * Calculate EAN-13/UPC-A check digit
 * @param {string} code - The barcode without check digit (12 digits for EAN-13, 11 for UPC-A)
 * @returns {number} - The calculated check digit
 */
export const calculateEAN13CheckDigit = (code) => {
  const digits = code.split('').map(Number);
  let sum = 0;
  
  for (let i = 0; i < digits.length; i++) {
    // For EAN-13: odd positions (0, 2, 4...) multiply by 1, even positions by 3
    sum += digits[i] * (i % 2 === 0 ? 1 : 3);
  }
  
  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit;
};

/**
 * Calculate EAN-8 check digit
 * @param {string} code - The barcode without check digit (7 digits)
 * @returns {number} - The calculated check digit
 */
export const calculateEAN8CheckDigit = (code) => {
  const digits = code.split('').map(Number);
  let sum = 0;
  
  for (let i = 0; i < digits.length; i++) {
    // For EAN-8: odd positions (0, 2, 4, 6) multiply by 3, even positions by 1
    sum += digits[i] * (i % 2 === 0 ? 3 : 1);
  }
  
  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit;
};

/**
 * Validate EAN-13 barcode (13 digits)
 * @param {string} code - The full 13-digit barcode
 * @returns {boolean} - Whether the barcode is valid
 */
export const validateEAN13 = (code) => {
  if (!/^\d{13}$/.test(code)) return false;
  
  const checkDigit = parseInt(code[12], 10);
  const calculatedCheckDigit = calculateEAN13CheckDigit(code.substring(0, 12));
  
  return checkDigit === calculatedCheckDigit;
};

/**
 * Validate EAN-8 barcode (8 digits)
 * @param {string} code - The full 8-digit barcode
 * @returns {boolean} - Whether the barcode is valid
 */
export const validateEAN8 = (code) => {
  if (!/^\d{8}$/.test(code)) return false;
  
  const checkDigit = parseInt(code[7], 10);
  const calculatedCheckDigit = calculateEAN8CheckDigit(code.substring(0, 7));
  
  return checkDigit === calculatedCheckDigit;
};

/**
 * Calculate UPC-A check digit
 * UPC-A: odd positions (1,3,5,7,9,11) × 3, even positions (2,4,6,8,10) × 1
 * @param {string} code - First 11 digits of UPC-A
 * @returns {number} - The calculated check digit
 */
export const calculateUPCACheckDigit = (code) => {
  const digits = code.split('').map(Number);
  let oddSum = 0;
  let evenSum = 0;
  
  for (let i = 0; i < digits.length; i++) {
    if ((i + 1) % 2 === 1) {
      // Odd position (1-indexed: 1,3,5,7,9,11)
      oddSum += digits[i];
    } else {
      // Even position (1-indexed: 2,4,6,8,10)
      evenSum += digits[i];
    }
  }
  
  const total = oddSum * 3 + evenSum;
  return (10 - (total % 10)) % 10;
};

/**
 * Validate UPC-A barcode (12 digits)
 * @param {string} code - The full 12-digit barcode
 * @returns {boolean} - Whether the barcode is valid
 */
export const validateUPCA = (code) => {
  if (!/^\d{12}$/.test(code)) return false;
  
  const checkDigit = parseInt(code[11], 10);
  const calculatedCheckDigit = calculateUPCACheckDigit(code.substring(0, 11));
  
  return checkDigit === calculatedCheckDigit;
};

/**
 * Validate any standard barcode format
 * @param {string} code - The barcode to validate
 * @returns {{ valid: boolean, format: string | null, message: string }}
 */
export const validateBarcode = (code) => {
  if (!code || typeof code !== 'string') {
    return { valid: false, format: null, message: 'Invalid input' };
  }
  
  const cleanCode = code.trim();
  
  // EAN-13 (13 digits)
  if (/^\d{13}$/.test(cleanCode)) {
    const isValid = validateEAN13(cleanCode);
    return {
      valid: isValid,
      format: 'EAN-13',
      message: isValid ? 'Valid EAN-13' : 'Invalid EAN-13 checksum'
    };
  }
  
  // EAN-8 (8 digits)
  if (/^\d{8}$/.test(cleanCode)) {
    const isValid = validateEAN8(cleanCode);
    return {
      valid: isValid,
      format: 'EAN-8',
      message: isValid ? 'Valid EAN-8' : 'Invalid EAN-8 checksum'
    };
  }
  
  // UPC-A (12 digits)
  if (/^\d{12}$/.test(cleanCode)) {
    const isValid = validateUPCA(cleanCode);
    return {
      valid: isValid,
      format: 'UPC-A',
      message: isValid ? 'Valid UPC-A' : 'Invalid UPC-A checksum'
    };
  }
  
  // Other formats (9-11 or 14 digits) - accept without checksum validation
  if (/^\d{9,14}$/.test(cleanCode)) {
    return {
      valid: true,
      format: 'Other',
      message: 'Accepted (no checksum validation for this format)'
    };
  }
  
  return {
    valid: false,
    format: null,
    message: 'Invalid barcode format (must be 8-14 digits)'
  };
};

/**
 * Barcode scan buffer for multi-scan confirmation
 * Requires multiple consistent reads before accepting a barcode
 */
export class ScanBuffer {
  constructor(requiredConfirmations = 5, maxAge = 3000) {
    this.scans = new Map(); // barcode -> { count, firstSeen, lastSeen }
    this.requiredConfirmations = requiredConfirmations;
    this.maxAge = maxAge; // milliseconds
  }
  
  /**
   * Add a scan to the buffer
   * @param {string} barcode - The scanned barcode
   * @returns {{ confirmed: boolean, barcode: string, count: number }}
   */
  addScan(barcode) {
    const now = Date.now();
    
    // Clean old scans
    this.cleanup(now);
    
    // Get or create entry
    const existing = this.scans.get(barcode);
    
    if (existing) {
      existing.count++;
      existing.lastSeen = now;
      
      if (existing.count >= this.requiredConfirmations) {
        // Barcode confirmed! Clear buffer and return
        this.scans.clear();
        return { confirmed: true, barcode, count: existing.count };
      }
      
      return { confirmed: false, barcode, count: existing.count };
    }
    
    // New barcode
    this.scans.set(barcode, {
      count: 1,
      firstSeen: now,
      lastSeen: now
    });
    
    return { confirmed: false, barcode, count: 1 };
  }
  
  /**
   * Clean old scans from buffer
   */
  cleanup(now = Date.now()) {
    for (const [barcode, data] of this.scans) {
      if (now - data.lastSeen > this.maxAge) {
        this.scans.delete(barcode);
      }
    }
  }
  
  /**
   * Clear the buffer
   */
  clear() {
    this.scans.clear();
  }
  
  /**
   * Get the most likely barcode (highest count)
   */
  getMostLikely() {
    let best = null;
    let bestCount = 0;
    
    for (const [barcode, data] of this.scans) {
      if (data.count > bestCount) {
        bestCount = data.count;
        best = barcode;
      }
    }
    
    return best ? { barcode: best, count: bestCount } : null;
  }
}

export default {
  validateBarcode,
  validateEAN13,
  validateEAN8,
  validateUPCA,
  calculateEAN13CheckDigit,
  calculateEAN8CheckDigit,
  ScanBuffer
};
